/**
 * Shared segmentation constants — colors, cursor, debounce timings.
 *
 * Centralises values used across VolumeViewport, SegmentationAnnotationPanel, and
 * the segmentation tools.
 */

import { logger } from "./logger";

// ---------------------------------------------------------------------------
// Colors (RGBA tuples for Cornerstone segmentation.config.color API)
// ---------------------------------------------------------------------------

/** Active segment overlay color — Tailwind purple-500 */
export const ACTIVE_SEGMENT_COLOR: [number, number, number, number] = [168, 85, 247, 255];

/** Previously-saved segment overlay color — Tailwind pink-500 */
export const SAVED_SEGMENT_COLOR: [number, number, number, number] = [236, 72, 153, 255];

/** Organ-mask overlay color — Tailwind blue-500. Distinct from the purple/pink
 *  annotation scheme so a whole-organ mask reads clearly as "not a annotation". */
export const ORGAN_SEGMENT_COLOR: [number, number, number, number] = [59, 130, 246, 255];

/** Reserved segment index for the organ mask — mirrors the backend's
 *  ORGAN_SEGMENT_INDEX. The FE claims this value client-side for the organ so the
 *  labelmap is self-describing AND the organ's index never dominates the annotation
 *  `max(segment_index)+1` sequence (which would push annotations to 101 and collide). */
export const ORGAN_SEGMENT_INDEX = 100;

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/** Delay (ms) before auto-accepting interpolated contours after a drawing event */
export const AUTO_ACCEPT_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Cursor
// ---------------------------------------------------------------------------
//
// All segmentation cursors follow the Cornerstone cursor layout:
//   - 32×32 SVG total
//   - Crosshair at (8,8) — that's the hotspot (click point)
//   - Tool-specific icon in the bottom-right quadrant, offset at (16,16)
//   - Purple (#a855f7) to match the segmentation overlay color
//
// This matches the size/position of Cornerstone's built-in tool cursors
// (RectangleROI, Length, etc.) so all tools feel consistent.

/** Helper: builds a 32×32 cursor SVG with crosshair at (8,8) and an icon group at (16,16). */
function buildSegCursor(iconSvg: string): string {
  const svg = [
    `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'>`,
    // Crosshair at (8,8)
    `<line x1='8' y1='0' x2='8' y2='16' stroke='#a855f7' stroke-width='1'/>`,
    `<line x1='0' y1='8' x2='16' y2='8' stroke='#a855f7' stroke-width='1'/>`,
    // Icon in bottom-right (translated to 16,16 origin, fits in 16×16 box)
    `<g transform='translate(16,16)'>`,
    iconSvg,
    `</g>`,
    `</svg>`,
  ].join("");
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 8 8, crosshair`;
}

/**
 * Lasso tool cursor — crosshair + freehand squiggle icon in bottom-right.
 */
export const SEGMENTATION_CURSOR_SVG = buildSegCursor(
  // Small freehand path (squiggle suggesting drawing)
  `<path d='M2,12 C4,6 7,2 10,4 C13,6 8,10 11,12 C14,14 13,8 14,6' fill='none' stroke='#a855f7' stroke-width='1.5' stroke-linecap='round' opacity='0.9'/>`,
);

/**
 * Bump tool cursor — crosshair + circle icon in bottom-right.
 * The icon is fixed-size (does not scale with brush radius).
 * The actual brush radius feedback comes from the tool overlay, not the cursor.
 *
 * Note: the bump tool still uses a radius-scaled cursor for the brush area indicator.
 * This function is the icon-style cursor; getBumpRadiusCursorSVG below is the radius one.
 */
export function getBumpCursorSVG(radius: number): string {
  // For the bump tool, we keep the radius-based cursor since it shows brush size.
  // This matches existing behavior — circle centered on cursor, size = brush diameter.
  const size = radius * 2;
  const cx = radius;
  const cy = radius;
  const r = Math.max(1, radius - 1.5);
  const svg = [
    `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>`,
    `<circle cx='${cx}' cy='${cy}' r='${r}' fill='none' stroke='#a855f7' stroke-width='1.5' opacity='0.9'/>`,
    `<line x1='${cx - 4}' y1='${cy}' x2='${cx + 4}' y2='${cy}' stroke='#a855f7' stroke-width='1'/>`,
    `<line x1='${cx}' y1='${cy - 4}' x2='${cx}' y2='${cy + 4}' stroke='#a855f7' stroke-width='1'/>`,
    `</svg>`,
  ].join("");
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${cx} ${cy}, crosshair`;
}

/**
 * Region grow tool cursor — crosshair + 3 concentric circles (bullseye) in bottom-right.
 */
export function getRegionGrowCursorSVG(): string {
  return buildSegCursor(
    // 3 concentric circles centered at (8,8) within the 16×16 icon area
    `<circle cx='8' cy='8' r='7' fill='none' stroke='#a855f7' stroke-width='1' opacity='0.7'/>` +
      `<circle cx='8' cy='8' r='4' fill='none' stroke='#a855f7' stroke-width='1' opacity='0.85'/>` +
      `<circle cx='8' cy='8' r='1.5' fill='#a855f7' opacity='0.9'/>`,
  );
}

/**
 * Positive refinement cursor — green crosshair (Shift+Click to include).
 */
export function getPositiveRefineCursorSVG(): string {
  const svg = [
    `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'>`,
    `<line x1='12' y1='2' x2='12' y2='22' stroke='#22c55e' stroke-width='2'/>`,
    `<line x1='2' y1='12' x2='22' y2='12' stroke='#22c55e' stroke-width='2'/>`,
    `<circle cx='12' cy='12' r='3' fill='#22c55e' opacity='0.5'/>`,
    `</svg>`,
  ].join("");
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, crosshair`;
}

/**
 * Negative refinement cursor — red crosshair (Ctrl+Click to exclude).
 */
export function getNegativeRefineCursorSVG(): string {
  const svg = [
    `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'>`,
    `<line x1='12' y1='2' x2='12' y2='22' stroke='#ef4444' stroke-width='2'/>`,
    `<line x1='2' y1='12' x2='22' y2='12' stroke='#ef4444' stroke-width='2'/>`,
    `<circle cx='12' cy='12' r='3' fill='#ef4444' opacity='0.5'/>`,
    `</svg>`,
  ].join("");
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, crosshair`;
}

// ---------------------------------------------------------------------------
// Color API utility
// ---------------------------------------------------------------------------

/**
 * Set a segment's display color via Cornerstone's segmentation config API.
 * Guards against the API being unavailable (varies across CS3D versions) and
 * skips a viewport that has no representation of the segmentation yet.
 */
export function setSegmentColor(
  segmentationConfig: unknown,
  viewportId: string,
  segmentationId: string,
  segmentIndex: number,
  color: [number, number, number, number],
): void {
  const colorApi = (segmentationConfig as any)?.color;
  if (!colorApi?.setSegmentIndexColor) return;
  // CS3D returns null when this viewport has no representation of the
  // segmentation yet (a pane still loading, or just swapped), and
  // setSegmentIndexColor would then write into null. Skip it; the next
  // recolor applies the color once the pane has its representation.
  if (
    typeof colorApi.getSegmentIndexColor === "function" &&
    colorApi.getSegmentIndexColor(viewportId, segmentationId, segmentIndex) == null
  ) {
    return;
  }
  try {
    colorApi.setSegmentIndexColor(viewportId, segmentationId, segmentIndex, color);
  } catch (e) {
    logger.debug(`[setSegmentColor] skipped for ${viewportId}:`, e);
  }
}
